import { parseChangeRequestSummary } from "@sd/lib/changeRequestTicketFields";
import type { AdminTicket, AdminTicketNote } from "@sd/types/admin";
import { normalizeTicketTypeFromApi } from "@sd/types/ticket";
import { normalizeStatus } from "@sd/constants/ticketStatuses";

function pickFirstTrimmed(...vals: unknown[]): string {
  for (const v of vals) {
    if (v == null) {
      continue;
    }
    const s = String(v).trim();
    if (s) {
      return s;
    }
  }
  return "";
}

function mapApiNote(n: Record<string, unknown>, i: number): AdminTicketNote {
  return {
    id: String(n.id ?? `n-${i}`),
    type: String(n.type ?? "public") === "internal" ? "internal" : "public",
    content: String(n.content ?? n.body ?? ""),
    author: String(n.author ?? n.user ?? "—"),
    createdAt: String(n.created_at ?? n.createdAt ?? new Date().toISOString()),
  };
}

/** Single cell value from grid / API (string, number, or `{ id, name }`). */
function normalizedAssignDisplay(v: unknown): string | null {
  if (v === undefined || v === null) {
    return null;
  }
  if (typeof v === "string") {
    const t = v.trim();
    return t === "" || t === "null" ? null : t;
  }
  if (typeof v === "number" || typeof v === "bigint") {
    return String(v);
  }
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    const combined = [o.first_name, o.last_name]
      .filter(Boolean)
      .map(String)
      .join(" ")
      .trim();
    const name =
      String(
        o.name ?? o.full_name ?? o.display_name ?? combined ?? ""
      ).trim() || String(o.email ?? "").trim();
    const idStr = o.id ?? o.pk ?? o.uuid ?? o.user_id ?? o.employee_id;
    const pick =
      name ||
      (idStr !== undefined && idStr !== null ? String(idStr).trim() : "");
    return pick === "" ? null : pick;
  }
  return null;
}

/**
 * Formily `grid_data` rows may use `assigned_to`, nested objects, or title-style keys.
 * Scan known keys first, then safe key patterns (skip "assignment…" false positives).
 */
export function pickAssignedFromGridRow(r: Record<string, unknown>): string | null {
  const direct: unknown[] = [
    r.assigned_to_name,
    r.assigned_name,
    r.assignee_name,
    r.assigned_agent_name,
    r.assigned_to_display,
    r.assignee_display_name,
    r.assigned_to,
    r.assigned,
    r.assignee,
    r.assignedTo,
    r.Assigned_To,
    r.assignee_id,
    r.assigned_to_id,
    r.assignedToId,
    r.assigned_user,
    r.assigned_user_id,
    r.assigned_agent,
    r.assigned_person,
    r.support_agent,
    r.agent_id,
  ];
  for (const d of direct) {
    const s = normalizedAssignDisplay(d);
    if (s) {
      return s;
    }
  }
  for (const [key, val] of Object.entries(r)) {
    if (
      !/^(assignee|assigned(_|$)|assign$)/i.test(key) &&
      !/^assigned_(to|user|agent|person)/i.test(key)
    ) {
      continue;
    }
    if (
      /sla|status|priority|tickettype|ticket_type|subject|description/i.test(
        key
      )
    ) {
      continue;
    }
    const s = normalizedAssignDisplay(val);
    if (s) {
      return s;
    }
  }
  return null;
}

const UUID_LIKE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const UUID_HEX_GROUPS =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function looksLikeUuid(s: string): boolean {
  const t = s.trim();
  return t.length >= 32 && UUID_LIKE.test(t);
}

/** GUID-shaped ids that strict RFC check rejects (still resolve via maps). */
export function looksLikeUuidLoose(s: string): boolean {
  const t = s.trim();
  return t.length >= 32 && UUID_HEX_GROUPS.test(t);
}

export function looksLikeOpaqueUserId(s: string): boolean {
  const t = s.trim();
  if (!t) {
    return false;
  }
  if (looksLikeUuid(t) || looksLikeUuidLoose(t)) {
    return true;
  }
  if (/^\d+$/.test(t) && t.length >= 1 && t.length <= 18) {
    return true;
  }
  return false;
}

/** Human project label from ticket_create / kanban-view (`project_name`). */
export function pickProjectNameFromApi(
  r: Record<string, unknown>
): string | undefined {
  const nested =
    r.project && typeof r.project === "object" && !Array.isArray(r.project)
      ? (r.project as Record<string, unknown>)
      : null;

  const candidates: unknown[] = [
    r.project_name,
    r.projectName,
    r.projectname,
    r.project_title,
    nested?.project_name,
    nested?.projectName,
    nested?.projectname,
    nested?.name,
    nested?.title,
  ];

  for (const value of candidates) {
    const name = String(value ?? "").trim();
    if (name && !looksLikeOpaqueUserId(name)) return name;
  }
  return undefined;
}

export function requesterLooksHuman(rq: unknown): rq is string {
  return (
    typeof rq === "string" &&
    rq.trim() !== "" &&
    !looksLikeOpaqueUserId(rq)
  );
}

function joinNameParts(a: unknown, b: unknown): string {
  const p = [a, b]
    .map(x => (typeof x === "string" ? x.trim() : ""))
    .filter(Boolean);
  return p.join(" ").trim();
}

function pickRequesterEmailFromRow(r: Record<string, unknown>): string {
  const nestedReq = r.requester;
  if (nestedReq && typeof nestedReq === "object" && !Array.isArray(nestedReq)) {
    const e = String((nestedReq as Record<string, unknown>).email ?? "").trim();
    if (e) {
      return e;
    }
  }
  const nestedUser =
    r.user && typeof r.user === "object" && !Array.isArray(r.user)
      ? (r.user as Record<string, unknown>)
      : null;
  if (nestedUser) {
    const e = String(nestedUser.email ?? "").trim();
    if (e) {
      return e;
    }
  }
  return String(
    r.requester_email ?? r.contact_email ?? r.email ?? r.user_email ?? ""
  ).trim();
}

export function pickRequesterDisplayFromRow(r: Record<string, unknown>): string {
  const nestedReq = r.requester;
  let fromNestedObj = "";
  if (nestedReq && typeof nestedReq === "object" && !Array.isArray(nestedReq)) {
    const o = nestedReq as Record<string, unknown>;
    fromNestedObj =
      joinNameParts(o.first_name ?? o.firstName, o.last_name ?? o.lastName) ||
      String(o.name ?? o.full_name ?? o.fullName ?? "").trim();
  }

  const nestedUser =
    r.user && typeof r.user === "object" && !Array.isArray(r.user)
      ? (r.user as Record<string, unknown>)
      : null;
  const fromUser = nestedUser
    ? joinNameParts(
        nestedUser.first_name ?? nestedUser.firstName,
        nestedUser.last_name ?? nestedUser.lastName
      ) ||
      String(
        nestedUser.name ?? nestedUser.full_name ?? nestedUser.fullName ?? ""
      ).trim()
    : "";

  let fromLinkedProfile = "";
  for (const key of [
    "end_user",
    "enduser",
    "portal_user",
    "submitter",
    "contact",
  ] as const) {
    const v = r[key];
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const o = v as Record<string, unknown>;
      const label =
        joinNameParts(o.first_name ?? o.firstName, o.last_name ?? o.lastName) ||
        String(
          o.name ?? o.full_name ?? o.fullName ?? o.fullname ?? ""
        ).trim();
      if (label) {
        fromLinkedProfile = label;
        break;
      }
    }
  }

  const flat: unknown[] = [
    r.requester_name,
    r.requesterName,
    r.created_by_name,
    r.createdByName,
    r.requester_display_name,
    r.requester_full_name,
    r.requester_label,
    r.end_user_name,
    r.portal_user_name,
    r.contact_name,
    r.reporter_name,
    r.user_name,
    r.full_name,
    r.display_name,
    r.customer_name,
    r.submitter_name,
    fromNestedObj,
    fromUser,
    fromLinkedProfile,
  ];
  for (const c of flat) {
    const s = typeof c === "string" ? c.trim() : "";
    if (s && !looksLikeOpaqueUserId(s)) {
      return s;
    }
  }

  const email = pickRequesterEmailFromRow(r);
  if (email.includes("@")) {
    const local = email.split("@")[0].replace(/[._]+/g, " ").trim();
    if (local) {
      return local;
    }
  }

  /** Portal / Formily often stores auth user on `created_by` while `requester` is empty — still need id for Enduser map. */
  const opaqueId = pickFirstTrimmed(
    typeof nestedReq === "string" ? nestedReq : undefined,
    typeof r.end_user === "string" ? r.end_user : undefined,
    typeof r.enduser === "string" ? r.enduser : undefined,
    typeof r.portal_user === "string" ? r.portal_user : undefined,
    r.requester_id,
    r.requesterId,
    r.userId,
    r.user_id,
    r.created_by,
    r.createdBy,
    r.submitter,
    r.submitter_id,
    r.end_user_id,
    r.portal_user_id,
    r.account_id
  );

  if (opaqueId) {
    return opaqueId;
  }

  if (typeof nestedReq === "string") {
    const t = nestedReq.trim();
    if (t) {
      return t;
    }
  }

  return String(r.requester ?? "").trim();
}

export function normalizeAdminStatus(s: unknown): AdminTicket["status"] {
  return normalizeStatus(s);
}

export function normalizeAdminPriority(p: unknown): AdminTicket["priority"] {
  const raw = String(p ?? "P3").toUpperCase();
  const lead = raw.match(/^P\s*([1-4])/);
  if (lead) {
    return `P${lead[1]}` as AdminTicket["priority"];
  }
  if (/CRITICAL|^1$/i.test(raw)) {
    return "P1";
  }
  if (/HIGH|^2$/i.test(raw)) {
    return "P2";
  }
  return "P3";
}

export function normalizeAdminSla(s: unknown): AdminTicket["slaStatus"] {
  const t = String(s ?? "").toLowerCase();
  if (t.includes("breach")) {
    return "breached";
  }
  if (t.includes("risk")) {
    return "at_risk";
  }
  return "on_track";
}

/** Toggle defaults OFF — only ON when API explicitly sends `is_sla: true`. */
export function resolveIsSlaFromRow(r: Record<string, unknown>): boolean {
  return r.is_sla === true || r.is_sla === "true" || r.is_sla === 1;
}

export function normalizeTicketType(t: unknown): AdminTicket["ticketType"] {
  if (t === undefined || t === null || String(t).trim() === "") {
    return "issue";
  }
  return normalizeTicketTypeFromApi(String(t));
}

export function mapRawToAdminTicket(raw: unknown): AdminTicket {
  const r = (raw ?? {}) as Record<string, unknown>;
  const id =
    String(r.id ?? r.ticket_id ?? r.reference ?? r.pk ?? "").trim() ||
    `row-${typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : Date.now()}`;
  const assignedStr = pickAssignedFromGridRow(r);
  const notesRaw = r.notes ?? r.comments ?? r.activity;
  const notes: AdminTicketNote[] = Array.isArray(notesRaw)
    ? (notesRaw as unknown[]).map((n, i) =>
      mapApiNote((n ?? {}) as Record<string, unknown>, i)
    )
    : [];

  let resolvedOut: string | undefined;
  if (r.resolved_at ?? r.resolvedAt) {
    resolvedOut = String(r.resolved_at ?? r.resolvedAt);
  }

  let firstResp: string | undefined;
  if (r.first_response_at ?? r.firstResponseAt) {
    firstResp = String(r.first_response_at ?? r.firstResponseAt);
  }

  const rawSla = r.sla_status ?? r.sla ?? r.slaStatus;
  const hasSlaFromApi =
    rawSla !== undefined && rawSla !== null && String(rawSla).trim() !== "";

  const rawType = r.tickettype ?? r.ticket_type ?? r.type;
  const hasTicketTypeFromApi =
    rawType !== undefined && rawType !== null && String(rawType).trim() !== "";

  const uiRaw = r.ui_id ?? r.uiId ?? r.ticket_ui_id;
  const uiId =
    uiRaw !== undefined && uiRaw !== null && String(uiRaw).trim() !== ""
      ? String(uiRaw).trim()
      : undefined;

  return {
    id,
    uiId,
    subject: String(r.subject ?? r.title ?? r.summary ?? "").trim() || "—",
    requester: pickRequesterDisplayFromRow(r),
    requesterEmail: pickRequesterEmailFromRow(r),
    department: String(
      r.department ?? r.dept ?? r.user_department ?? ""
    ).trim(),
    assignedAgent: assignedStr,
    status: normalizeAdminStatus(r.status),
    priority: normalizeAdminPriority(r.priority),
    priorityId: r.priority_id ? String(r.priority_id).trim() : undefined,
    category: String(r.category ?? r.tickittype ?? "").trim(),
    ticketType: normalizeTicketType(hasTicketTypeFromApi ? rawType : undefined),
    ticketTypeRaw: hasTicketTypeFromApi ? String(rawType).trim() : undefined,
    slaStatus: normalizeAdminSla(hasSlaFromApi ? rawSla : undefined),
    hasSlaFromApi,
    hasTicketTypeFromApi,
    createdAt: String(
      r.created_at ??
        r.createdAt ??
        r.created_on ??
        r.date ??
        new Date().toISOString()
    ),
    updatedAt: String(
      r.updated_at ??
        r.updatedAt ??
        r.updated_on ??
        r.modified_at ??
        r.created_at ??
        r.created_on ??
        new Date().toISOString()
    ),
    createdBy: (() => {
      const named = pickFirstTrimmed(
        r.created_by_name,
        r.createdByName,
        r.created_by_display,
        r.creator_name
      );
      if (named && !looksLikeOpaqueUserId(named)) {
        return named;
      }
      const id = pickFirstTrimmed(r.created_by, r.createdBy, r.creator);
      return id || undefined;
    })(),
    resolvedAt: resolvedOut,
    deadline: typeof r.deadline === "string" && r.deadline.trim() !== "" 
      ? r.deadline.trim() 
      : undefined,
    channel: String(r.channel ?? "").trim(),
    tags: Array.isArray(r.tags) ? (r.tags as string[]) : [],
    firstResponseAt: firstResp,
    csatScore:
      typeof r.csat_score === "number"
        ? r.csat_score
        : typeof r.csatScore === "number"
          ? r.csatScore
          : undefined,
    is_vip: r.is_vip === true || r.is_vip === "true" || r.is_vip === 1,
    is_sla: resolveIsSlaFromRow(r),
    escalationLevel: Number(r.escalation_level ?? r.escalationLevel ?? 0) || 0,
    projectName: (() => {
      const pn = String(r.project_name ?? r.projectName ?? "").trim();
      return pn !== "" ? pn : undefined;
    })(),
    changeRequest: parseChangeRequestSummary(r) ?? undefined,
    notes,
    description: (() => {
      const d = r.description ?? r.details ?? r.body;
      const t = String(d ?? "").trim();
      return t === "" ? undefined : t;
    })(),
    attachments: (() => {
      const a = r.attachments;
      if (a === null || a === undefined) return null;
      const s = String(a).trim();
      return s === "" || s === "[]" || s === "null" ? null : s;
    })(),
  };
}

/** Overlay GET `/dynamic-model/{model}/{id}/` onto grid (`ui_id` often missing from grid_data). */
export function mergeGridTicketWithDetailPayload(
  grid: AdminTicket,
  detail: unknown
): AdminTicket {
  const parsed = mapRawToAdminTicket(detail);
  // If detail gives back a UUID for requester but grid already has a human name, keep the grid's name
  const requester =
    looksLikeOpaqueUserId(parsed.requester) && requesterLooksHuman(grid.requester)
      ? grid.requester
      : parsed.requester || grid.requester;
  return {
    ...grid,
    ...parsed,
    id: grid.id,
    requester,
    notes:
      parsed.notes.length > 0
        ? parsed.notes
        : grid.notes.length > 0
          ? grid.notes
          : parsed.notes,
  };
}
